import {Component, OnInit, QueryList, ViewChild, ViewChildren} from '@angular/core';
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {DateValidation} from '../../validators/dateValidation'
import {AgGridAngular} from 'ag-grid-angular';
import {localeSwedish} from '../locale.swedish';
import {ToastrService} from 'ngx-toastr';
import {Location} from '../../model/location';
import {transferDetails, transferReport} from '../../model/report/transferReport';
import * as moment from 'moment';
import {activityAbbreviations, BusEnum} from '../../common/enums';
import * as XLSX from 'xlsx';
import {ISpinnerConfig, NgxSpinner, SPINNER_ANIMATIONS, SPINNER_PLACEMENT} from '@hardpool/ngx-spinner';
import {ReportActivity} from '../../model/reportActivity';
import {LocationService} from '../../services/location.service';
import {ReportService} from '../../services/report.service';

@Component({
  selector: 'transfer',
  templateUrl: 'transfer.component.html',
  styleUrls: ['transfer.component.css'],
})
export class TransferComponent implements OnInit {

  @ViewChildren('activities') activityGrids: QueryList<AgGridAngular>;
  @ViewChild("spinner", { static: true }) spinner: NgxSpinner;

  searchForm: UntypedFormGroup;
  busy: boolean = false;
  noMatch: boolean = false;
  performedSearch: boolean = false;

  maxDate: Date = new Date();

  locations: Location[];
  transferReport: transferReport;

  dateControl: UntypedFormControl;
  transferControl: UntypedFormControl;

  columnDefs = [
    {
      headerName: 'Status', field: 'Type', width: 180, sortingOrder: ["asc", "desc"],
      cellRenderer: (result) => {
        let abb = activityAbbreviations.find(x => x.state == result.data.Type);
        return abb ? abb.translated : result.data.type;
      }
    },
    {
      headerName: 'Tid', field: 'started', width: 150, sort: { direction: 'asc', priority: 1 }, sortingOrder: ["asc", "desc"],
      cellRenderer: (result) => { return result.data.started ? this.getTime(result.data.started) : ""; }
    }
  ];

  spinnerConfig: ISpinnerConfig;

  localeText = {};
  rowData = [];
  matchingFlights = [];

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly toaster: ToastrService,
    private readonly locationService: LocationService,
    private readonly reportService: ReportService) {

    this.dateControl = new UntypedFormControl([Validators.required, DateValidation()]);
    this.transferControl = new UntypedFormControl('', Validators.required);

    this.localeText = localeSwedish;

    this.searchForm = this.fb.group(
      {
        date: this.dateControl,
        transfer: this.transferControl
      });

  }

  ngOnInit() {

    this.spinnerConfig = {
      placement: SPINNER_PLACEMENT.block_ui,
      animation: SPINNER_ANIMATIONS.horizontal_bounce,
      size: "2rem",
      color: "#7AB800",
      bgColor: "transparent"
    };

    this.locations = this.locationService.Locations.filter(x => x.type == "T");
  }

  async delay(ms: number) {
    await new Promise<void>(resolve => setTimeout(() => resolve(), ms)).then(() => console.log());
  }

  canSearch() {
    return this.searchForm.valid && !this.busy;
  }

  export() {
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    let index: number = 1;
    this.transferReport.transferDetails.forEach((detail: transferDetails) => {

      //TODO : check why this is necessaary ??
      detail.activities.forEach(act => {
        act.started = act.started ? moment(act.started).format("YYYY-MM-DD HH:mm:ss").toLocaleString() : "";
        act.ended = act.ended ? moment(act.ended).format("YYYY-MM-DD HH:mm:ss").toLocaleString() : "";
      });

      const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(detail.activities);

      ws['A1'].v = "Status";
      ws['B1'].v = "Tid";
      ws['B1'].s =
      {
        fill: {
          patternType: 'solid', // none / solid
          fgColor: { rgb: 'FFD3D3D3' },
          bgColor: { rgb: 'FFD3D3D3' }
        }
      }

      ws['!cols'] = [];
      // hide ended column
      ws["!cols"][2] = { hidden: true };
      let driver = detail.driver == undefined ? "Ej tilldelad" : detail.driver;
      XLSX.utils.book_append_sheet(wb, ws, index.toString() + "-" + driver);
      index++;
    });

    let date = new Date(this.dateControl.value);
    XLSX.writeFile(wb, this.transferControl.value.name + " - " + date.toDateString() + ".xlsx");
  }

  searchMatchingFlight(flight) {
    this.transferControl.setValue(flight);
    this.search();
  }

  canExport() {
    return this.transferReport && this.transferReport.transferDetails.length > 0 && !this.busy;
  }

  missingActivities() {
    return !this.transferReport || this.transferReport.transferDetails.length == 0;
  }

  async search() {
    if (this.searchForm.valid) {
      this.spinner.show();
      let date = new Date(this.dateControl.value);
      let transfer: Location = this.transferControl.value;
      let rows = [];
      this.busy = true;
      this.noMatch = false;
      this.performedSearch = true;
      try {
        this.transferReport = await this.reportService.gettransferReport(date.toISOString(), transfer.name);
        this.transferReport.transferDetails = this.sortReports(this.transferReport.transferDetails);
        this.processReport(this.transferReport.transferDetails);

        this.noMatch = this.transferReport.transferDetails.length == 0;
        this.busy = false;
        this.rowData = rows;
        this.spinner.hide();

      }
      catch (error) {
        this.spinner.hide();
        this.noMatch = error.request.status == 204;
        this.busy = false;
        this.rowData = [];
        if (!this.noMatch)
          this.showError("Ett fel uppstod vid inläsning av data.")
      }
    }
  }


  processReport(details: transferDetails[]) {
    details.forEach((detail: transferDetails) => {
      detail.activities = detail.activities.filter(x => x.started != null);

    });
  }

  sortReports(input: any): any {
    return input.sort((a: transferDetails, b: transferDetails) => {
      let aStart = this.getTime(a.date);
      let bStart = this.getTime(b.date);
      if (aStart < bStart)
        return -1;
      else if (aStart > bStart)
        return 1;
      else
        return 0;
    });
  }

  hasNoshow(status: ReportActivity[]): boolean {
    let index = status.findIndex(x => x.type == BusEnum.NoShow);
    return index >= 0;
  }

  getTime(date) {
    return moment(date).format('HH:mm');
  }

  showError(message: string) {
    this.toaster.error(message);
  }

}
